import {
  createAnalytics, clarityPlugin, type AnalyticsFacade, type PersistentTags,
  bucketBalance,
} from '@aurumrun/analytics'

/**
 * Shared analytics glue (client-only). Inherited by web/preview/landing via
 * layers/base. No-ops when NUXT_PUBLIC_CLARITY_PROJECT_ID is unset.
 *
 * Replaces apps/web/app/plugins/clarity.client.ts — identity + tags now flow
 * through the vendor-agnostic facade so a second provider can be added later.
 */
export default defineNuxtPlugin({
  name: 'analytics',
  parallel: true, // analytics must never block other plugins
  setup() {
    const config = useRuntimeConfig()

    // Hard off-switch (admin panel). Bail before wiring any identify/track
    // watchers so no staff action ever reaches a provider, regardless of what
    // NUXT_PUBLIC_CLARITY_PROJECT_ID resolves to. We still provide a no-op
    // facade so any `$analytics` consumer keeps working.
    if (config.public.analyticsDisabled) {
      return { provide: { analytics: createAnalytics({ app: 'aurumrun', plugins: [] }) } }
    }

    const projectId = (config.public.clarityProjectId as string) || ''

    // Runtime kill switch (admin /config → analytics.clarity_enabled). Fail-open:
    // only web/preview have useGameConfig (layers/game); landing has no game layer.
    let clarityEnabled = true
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (typeof (useGameConfig as any) === 'function') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { config: gameConfig } = (useGameConfig as any)()
        clarityEnabled = gameConfig.value?.analytics?.clarityEnabled !== false
      }
    } catch { /* fail-open: keep clarityEnabled = true */ }

    const effectiveProjectId = clarityEnabled ? projectId : ''

    const analytics: AnalyticsFacade = createAnalytics({
      app: 'aurumrun',
      plugins: [clarityPlugin({ projectId: effectiveProjectId })],
    })

    // Stores may not exist in every app (landing has no auth/visitor store).
    // `typeof useX === 'function'` guards are safe in JS even when the symbol
    // is not defined; each store is also wrapped in its own try/catch so a
    // ReferenceError from one store doesn't block the others.

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let userStore: { user?: { id?: string } | null } | null = null
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (typeof (useUserStore as any) === 'function') userStore = (useUserStore as any)() as { user?: { id?: string } | null }
    } catch { /* landing / preview don't have this store */ }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let visitorStore: { visitorId?: string | null } | null = null
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (typeof (useVisitorStore as any) === 'function') visitorStore = (useVisitorStore as any)() as { visitorId?: string | null }
    } catch { /* landing / preview don't have this store */ }

    const deviceType: PersistentTags['deviceType'] =
      typeof navigator !== 'undefined' && /Mobi|Android/i.test(navigator.userAgent)
        ? 'mobile' : 'desktop'

    // Attribution captured pre-signup (ref_code/ref_utm cookies via useReferral).
    function attributionTags(): Partial<PersistentTags> {
      const ref = useCookie<string | null>('ref_code').value || undefined
      let utm: { source?: string; medium?: string; campaign?: string } = {}
      try { utm = JSON.parse(useCookie<string | null>('ref_utm').value || '{}') } catch { /* ignore */ }
      return {
        referredBy: ref,
        utmSource: utm.source || undefined,
        utmMedium: utm.medium || undefined,
        utmCampaign: utm.campaign || undefined,
        deviceType,
      }
    }

    // Sticky cross-session demo flag (set by stores/demoBet.ts on first demo
    // bet). Rides into identify so a demo player who later registers is
    // segmentable by whether they demoed first (Demo Player → Registered funnel).
    function playedDemoTag(): Partial<PersistentTags> {
      try {
        return localStorage.getItem('aurum_played_demo') === 'true' ? { playedDemo: 'true' } : {}
      } catch { return {} }
    }

    // Resolve account-state tags from stores if present (web only).
    function resolveAccountTags(): Partial<PersistentTags> {
      const out: Partial<PersistentTags> = {}
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const tier = (typeof (useWealthTierStore as any) === 'function' ? (useWealthTierStore as any)() : null) as
          | { tierSlug?: PersistentTags['accountTier'] } | null
        if (tier?.tierSlug) out.accountTier = tier.tierSlug
      } catch { /* fail-soft */ }
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const fb = (typeof (useFreeBetsStore as any) === 'function' ? (useFreeBetsStore as any)() : null) as
          | { hasFreeBets?: boolean } | null
        if (fb) out.hasFreeBets = fb.hasFreeBets ? 'true' : 'false'
      } catch { /* fail-soft */ }
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const bal = (typeof (useBalanceStore as any) === 'function' ? (useBalanceStore as any)() : null) as
          | { available?: number } | null
        if (typeof bal?.available === 'number') out.balanceBucket = bucketBalance(bal.available)
      } catch { /* fail-soft */ }
      return out
    }

    // Authenticated user → identify by userId with full persistent traits.
    if (userStore) {
      watch(
        () => userStore!.user?.id,
        (userId) => {
          if (!userId) return
          analytics.identify(userId, {
            userId,
            userType: 'authenticated',
            ...attributionTags(),
            ...playedDemoTag(),
            ...resolveAccountTags(),
          })
        },
        { immediate: true },
      )
    }

    // Anonymous visitor → identify by visitorId (only while signed out).
    if (visitorStore) {
      watch(
        () => visitorStore!.visitorId,
        (visitorId) => {
          if (!visitorId || userStore?.user?.id) return
          analytics.identify(visitorId, { visitorId, userType: 'visitor', ...attributionTags(), ...playedDemoTag() })
        },
        { immediate: true },
      )
    }

    return { provide: { analytics } }
  },
})
